// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  AccountProfileUpdateStatusCreateBodyOne,
  AccountProfileUpdateStatusCreateBodyThree,
  AccountProfileUploadBackgroundUpdate400,
  AccountProfileUploadBackgroundUpdate500,
  BackgroundPicture,
  NotificationPreferences,
  PatchedNotificationPreferences,
  ProfilePicture,
  ProfileUpdate,
  PublicUserProfile,
  User,
  UserStatusUpdate,
} from "../../models";
import { customInstance } from "../../custom-instance";

// https://stackoverflow.com/questions/49579094/typescript-conditional-types-filter-out-readonly-properties-pick-only-requir/49579497#49579497
type IfEquals<X, Y, A = X, B = never> = (<T>() => T extends X ? 1 : 2) extends <
  T
>() => T extends Y ? 1 : 2
  ? A
  : B;

type WritableKeys<T> = {
  [P in keyof T]-?: IfEquals<
    { [Q in P]: T[P] },
    { -readonly [Q in P]: T[P] },
    P
  >;
}[keyof T];

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I
) => void
  ? I
  : never;
type DistributeReadOnlyOverUnions<T> = T extends any ? NonReadonly<T> : never;

type Writable<T> = Pick<T, WritableKeys<T>>;
type NonReadonly<T> = [T] extends [UnionToIntersection<T>]
  ? {
      [P in keyof Writable<T>]: T[P] extends object
        ? NonReadonly<NonNullable<T[P]>>
        : T[P];
    }
  : DistributeReadOnlyOverUnions<T>;

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Get authenticated user's information and state
 */
export const accountMeRetrieve = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<User>(
    { url: `/api/account/me/`, method: "GET", signal },
    options
  );
};

export const getAccountMeRetrieveQueryKey = () => {
  return [`/api/account/me/`] as const;
};

export const getAccountMeRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof accountMeRetrieve>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountMeRetrieve>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAccountMeRetrieveQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof accountMeRetrieve>>
  > = ({ signal }) => accountMeRetrieve(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof accountMeRetrieve>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AccountMeRetrieveQueryResult = NonNullable<
  Awaited<ReturnType<typeof accountMeRetrieve>>
>;
export type AccountMeRetrieveQueryError = unknown;

export function useAccountMeRetrieve<
  TData = Awaited<ReturnType<typeof accountMeRetrieve>>,
  TError = unknown
>(options: {
  query: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountMeRetrieve>>,
      TError,
      TData
    >
  > &
    Pick<
      DefinedInitialDataOptions<
        Awaited<ReturnType<typeof accountMeRetrieve>>,
        TError,
        Awaited<ReturnType<typeof accountMeRetrieve>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountMeRetrieve<
  TData = Awaited<ReturnType<typeof accountMeRetrieve>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountMeRetrieve>>,
      TError,
      TData
    >
  > &
    Pick<
      UndefinedInitialDataOptions<
        Awaited<ReturnType<typeof accountMeRetrieve>>,
        TError,
        Awaited<ReturnType<typeof accountMeRetrieve>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountMeRetrieve<
  TData = Awaited<ReturnType<typeof accountMeRetrieve>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountMeRetrieve>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useAccountMeRetrieve<
  TData = Awaited<ReturnType<typeof accountMeRetrieve>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountMeRetrieve>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getAccountMeRetrieveQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get current notification preferences
 */
export const accountProfileNotificationPreferencesRetrieve = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<NotificationPreferences>(
    {
      url: `/api/account/profile/notification-preferences/`,
      method: "GET",
      signal,
    },
    options
  );
};

export const getAccountProfileNotificationPreferencesRetrieveQueryKey = () => {
  return [`/api/account/profile/notification-preferences/`] as const;
};

export const getAccountProfileNotificationPreferencesRetrieveQueryOptions = <
  TData = Awaited<
    ReturnType<typeof accountProfileNotificationPreferencesRetrieve>
  >,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountProfileNotificationPreferencesRetrieve>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getAccountProfileNotificationPreferencesRetrieveQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof accountProfileNotificationPreferencesRetrieve>>
  > = ({ signal }) =>
    accountProfileNotificationPreferencesRetrieve(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof accountProfileNotificationPreferencesRetrieve>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AccountProfileNotificationPreferencesRetrieveQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof accountProfileNotificationPreferencesRetrieve>>
  >;
export type AccountProfileNotificationPreferencesRetrieveQueryError = unknown;

export function useAccountProfileNotificationPreferencesRetrieve<
  TData = Awaited<
    ReturnType<typeof accountProfileNotificationPreferencesRetrieve>
  >,
  TError = unknown
>(options: {
  query: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountProfileNotificationPreferencesRetrieve>>,
      TError,
      TData
    >
  > &
    Pick<
      DefinedInitialDataOptions<
        Awaited<
          ReturnType<typeof accountProfileNotificationPreferencesRetrieve>
        >,
        TError,
        Awaited<
          ReturnType<typeof accountProfileNotificationPreferencesRetrieve>
        >
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountProfileNotificationPreferencesRetrieve<
  TData = Awaited<
    ReturnType<typeof accountProfileNotificationPreferencesRetrieve>
  >,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountProfileNotificationPreferencesRetrieve>>,
      TError,
      TData
    >
  > &
    Pick<
      UndefinedInitialDataOptions<
        Awaited<
          ReturnType<typeof accountProfileNotificationPreferencesRetrieve>
        >,
        TError,
        Awaited<
          ReturnType<typeof accountProfileNotificationPreferencesRetrieve>
        >
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountProfileNotificationPreferencesRetrieve<
  TData = Awaited<
    ReturnType<typeof accountProfileNotificationPreferencesRetrieve>
  >,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountProfileNotificationPreferencesRetrieve>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useAccountProfileNotificationPreferencesRetrieve<
  TData = Awaited<
    ReturnType<typeof accountProfileNotificationPreferencesRetrieve>
  >,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountProfileNotificationPreferencesRetrieve>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getAccountProfileNotificationPreferencesRetrieveQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update notification preferences (partial update allowed)
 */
export const accountProfileNotificationPreferencesPartialUpdate = (
  patchedNotificationPreferences: PatchedNotificationPreferences,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<NotificationPreferences>(
    {
      url: `/api/account/profile/notification-preferences/`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: patchedNotificationPreferences,
    },
    options
  );
};

export const getAccountProfileNotificationPreferencesPartialUpdateMutationOptions =
  <TError = unknown, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof accountProfileNotificationPreferencesPartialUpdate>
      >,
      TError,
      { data: PatchedNotificationPreferences },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof accountProfileNotificationPreferencesPartialUpdate>
    >,
    TError,
    { data: PatchedNotificationPreferences },
    TContext
  > => {
    const mutationKey = ["accountProfileNotificationPreferencesPartialUpdate"];
    const { mutation: mutationOptions, request: requestOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey }, request: undefined };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof accountProfileNotificationPreferencesPartialUpdate>
      >,
      { data: PatchedNotificationPreferences }
    > = (props) => {
      const { data } = props ?? {};

      return accountProfileNotificationPreferencesPartialUpdate(
        data,
        requestOptions
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type AccountProfileNotificationPreferencesPartialUpdateMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof accountProfileNotificationPreferencesPartialUpdate>
    >
  >;
export type AccountProfileNotificationPreferencesPartialUpdateMutationBody =
  PatchedNotificationPreferences;
export type AccountProfileNotificationPreferencesPartialUpdateMutationError =
  unknown;

export const useAccountProfileNotificationPreferencesPartialUpdate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<typeof accountProfileNotificationPreferencesPartialUpdate>
    >,
    TError,
    { data: PatchedNotificationPreferences },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<
    ReturnType<typeof accountProfileNotificationPreferencesPartialUpdate>
  >,
  TError,
  { data: PatchedNotificationPreferences },
  TContext
> => {
  const mutationOptions =
    getAccountProfileNotificationPreferencesPartialUpdateMutationOptions(
      options
    );

  return useMutation(mutationOptions);
};
/**
 * Update user profile information
 */
export const accountProfileUpdateCreate = (
  profileUpdate: NonReadonly<ProfileUpdate>,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ProfileUpdate>(
    {
      url: `/api/account/profile/update/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: profileUpdate,
      signal,
    },
    options
  );
};

export const getAccountProfileUpdateCreateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountProfileUpdateCreate>>,
    TError,
    { data: NonReadonly<ProfileUpdate> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountProfileUpdateCreate>>,
  TError,
  { data: NonReadonly<ProfileUpdate> },
  TContext
> => {
  const mutationKey = ["accountProfileUpdateCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountProfileUpdateCreate>>,
    { data: NonReadonly<ProfileUpdate> }
  > = (props) => {
    const { data } = props ?? {};

    return accountProfileUpdateCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountProfileUpdateCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountProfileUpdateCreate>>
>;
export type AccountProfileUpdateCreateMutationBody = NonReadonly<ProfileUpdate>;
export type AccountProfileUpdateCreateMutationError = unknown;

export const useAccountProfileUpdateCreate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountProfileUpdateCreate>>,
    TError,
    { data: NonReadonly<ProfileUpdate> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountProfileUpdateCreate>>,
  TError,
  { data: NonReadonly<ProfileUpdate> },
  TContext
> => {
  const mutationOptions = getAccountProfileUpdateCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Update user's status message
 */
export const accountProfileUpdateStatusCreate = (
  accountProfileUpdateStatusCreateBody:
    | AccountProfileUpdateStatusCreateBodyOne
    | unknown
    | AccountProfileUpdateStatusCreateBodyThree,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<UserStatusUpdate>(
    {
      url: `/api/account/profile/update-status/`,
      method: "POST",
      data: accountProfileUpdateStatusCreateBody,
      signal,
    },
    options
  );
};

export const getAccountProfileUpdateStatusCreateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountProfileUpdateStatusCreate>>,
    TError,
    {
      data:
        | AccountProfileUpdateStatusCreateBodyOne
        | unknown
        | AccountProfileUpdateStatusCreateBodyThree;
    },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountProfileUpdateStatusCreate>>,
  TError,
  {
    data:
      | AccountProfileUpdateStatusCreateBodyOne
      | unknown
      | AccountProfileUpdateStatusCreateBodyThree;
  },
  TContext
> => {
  const mutationKey = ["accountProfileUpdateStatusCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountProfileUpdateStatusCreate>>,
    {
      data:
        | AccountProfileUpdateStatusCreateBodyOne
        | unknown
        | AccountProfileUpdateStatusCreateBodyThree;
    }
  > = (props) => {
    const { data } = props ?? {};

    return accountProfileUpdateStatusCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountProfileUpdateStatusCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountProfileUpdateStatusCreate>>
>;
export type AccountProfileUpdateStatusCreateMutationBody =
  | AccountProfileUpdateStatusCreateBodyOne
  | unknown
  | AccountProfileUpdateStatusCreateBodyThree;
export type AccountProfileUpdateStatusCreateMutationError = unknown;

export const useAccountProfileUpdateStatusCreate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountProfileUpdateStatusCreate>>,
    TError,
    {
      data:
        | AccountProfileUpdateStatusCreateBodyOne
        | unknown
        | AccountProfileUpdateStatusCreateBodyThree;
    },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountProfileUpdateStatusCreate>>,
  TError,
  {
    data:
      | AccountProfileUpdateStatusCreateBodyOne
      | unknown
      | AccountProfileUpdateStatusCreateBodyThree;
  },
  TContext
> => {
  const mutationOptions =
    getAccountProfileUpdateStatusCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Upload or update user background picture
 */
export const accountProfileUploadBackgroundUpdate = (
  backgroundPicture: NonReadonly<BackgroundPicture>,
  options?: SecondParameter<typeof customInstance>
) => {
  const formData = new FormData();
  formData.append("file", backgroundPicture.file);
  formData.append(
    "background_picture_url",
    backgroundPicture.background_picture_url
  );

  return customInstance<BackgroundPicture>(
    {
      url: `/api/account/profile/upload-background/`,
      method: "PUT",
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
    },
    options
  );
};

export const getAccountProfileUploadBackgroundUpdateMutationOptions = <
  TError =
    | AccountProfileUploadBackgroundUpdate400
    | AccountProfileUploadBackgroundUpdate500,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountProfileUploadBackgroundUpdate>>,
    TError,
    { data: NonReadonly<BackgroundPicture> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountProfileUploadBackgroundUpdate>>,
  TError,
  { data: NonReadonly<BackgroundPicture> },
  TContext
> => {
  const mutationKey = ["accountProfileUploadBackgroundUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountProfileUploadBackgroundUpdate>>,
    { data: NonReadonly<BackgroundPicture> }
  > = (props) => {
    const { data } = props ?? {};

    return accountProfileUploadBackgroundUpdate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountProfileUploadBackgroundUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountProfileUploadBackgroundUpdate>>
>;
export type AccountProfileUploadBackgroundUpdateMutationBody =
  NonReadonly<BackgroundPicture>;
export type AccountProfileUploadBackgroundUpdateMutationError =
  | AccountProfileUploadBackgroundUpdate400
  | AccountProfileUploadBackgroundUpdate500;

export const useAccountProfileUploadBackgroundUpdate = <
  TError =
    | AccountProfileUploadBackgroundUpdate400
    | AccountProfileUploadBackgroundUpdate500,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountProfileUploadBackgroundUpdate>>,
    TError,
    { data: NonReadonly<BackgroundPicture> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountProfileUploadBackgroundUpdate>>,
  TError,
  { data: NonReadonly<BackgroundPicture> },
  TContext
> => {
  const mutationOptions =
    getAccountProfileUploadBackgroundUpdateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Upload or update user profile picture
 */
export const accountProfileUploadPictureUpdate = (
  profilePicture: NonReadonly<ProfilePicture>,
  options?: SecondParameter<typeof customInstance>
) => {
  const formData = new FormData();
  formData.append("file", profilePicture.file);
  formData.append("profile_picture_url", profilePicture.profile_picture_url);

  return customInstance<ProfilePicture>(
    {
      url: `/api/account/profile/upload-picture/`,
      method: "PUT",
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
    },
    options
  );
};

export const getAccountProfileUploadPictureUpdateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountProfileUploadPictureUpdate>>,
    TError,
    { data: NonReadonly<ProfilePicture> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountProfileUploadPictureUpdate>>,
  TError,
  { data: NonReadonly<ProfilePicture> },
  TContext
> => {
  const mutationKey = ["accountProfileUploadPictureUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountProfileUploadPictureUpdate>>,
    { data: NonReadonly<ProfilePicture> }
  > = (props) => {
    const { data } = props ?? {};

    return accountProfileUploadPictureUpdate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountProfileUploadPictureUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountProfileUploadPictureUpdate>>
>;
export type AccountProfileUploadPictureUpdateMutationBody =
  NonReadonly<ProfilePicture>;
export type AccountProfileUploadPictureUpdateMutationError = unknown;

export const useAccountProfileUploadPictureUpdate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountProfileUploadPictureUpdate>>,
    TError,
    { data: NonReadonly<ProfilePicture> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountProfileUploadPictureUpdate>>,
  TError,
  { data: NonReadonly<ProfilePicture> },
  TContext
> => {
  const mutationOptions =
    getAccountProfileUploadPictureUpdateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Mark the software-welcome notice as seen.
 */
export const accountWelcomeDismissCreate = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<User>(
    { url: `/api/account/welcome/dismiss/`, method: "POST", signal },
    options
  );
};

export const getAccountWelcomeDismissCreateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountWelcomeDismissCreate>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountWelcomeDismissCreate>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["accountWelcomeDismissCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountWelcomeDismissCreate>>,
    void
  > = () => {
    return accountWelcomeDismissCreate(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountWelcomeDismissCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountWelcomeDismissCreate>>
>;

export type AccountWelcomeDismissCreateMutationError = unknown;

export const useAccountWelcomeDismissCreate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountWelcomeDismissCreate>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountWelcomeDismissCreate>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions =
    getAccountWelcomeDismissCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Get authenticated user's profile
 */
export const usersRetrieve = (
  slug: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<PublicUserProfile>(
    { url: `/api/users/${slug}/`, method: "GET", signal },
    options
  );
};

export const getUsersRetrieveQueryKey = (slug: string) => {
  return [`/api/users/${slug}/`] as const;
};

export const getUsersRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof usersRetrieve>>,
  TError = unknown
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof usersRetrieve>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUsersRetrieveQueryKey(slug);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof usersRetrieve>>> = ({
    signal,
  }) => usersRetrieve(slug, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof usersRetrieve>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UsersRetrieveQueryResult = NonNullable<
  Awaited<ReturnType<typeof usersRetrieve>>
>;
export type UsersRetrieveQueryError = unknown;

export function useUsersRetrieve<
  TData = Awaited<ReturnType<typeof usersRetrieve>>,
  TError = unknown
>(
  slug: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof usersRetrieve>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof usersRetrieve>>,
          TError,
          Awaited<ReturnType<typeof usersRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useUsersRetrieve<
  TData = Awaited<ReturnType<typeof usersRetrieve>>,
  TError = unknown
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof usersRetrieve>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof usersRetrieve>>,
          TError,
          Awaited<ReturnType<typeof usersRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useUsersRetrieve<
  TData = Awaited<ReturnType<typeof usersRetrieve>>,
  TError = unknown
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof usersRetrieve>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useUsersRetrieve<
  TData = Awaited<ReturnType<typeof usersRetrieve>>,
  TError = unknown
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof usersRetrieve>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getUsersRetrieveQueryOptions(slug, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
